import {
  acknowledgeApprovalRequest as acknowledgeApprovalRequestApi,
  acceptApprovalRequest as acceptApprovalRequestApi,
  cancelApprovalRequest as cancelApprovalRequestApi,
  createGroupMemberConnectionApprovalRequest as createGroupMemberConnectionApprovalRequestApi,
  getApprovalRequestList as getApprovalRequestListApi,
  rejectApprovalRequest as rejectApprovalRequestApi,
} from '../../api/approvalRequestApi';
import { ApprovalRequestData } from '../../dto/EntityRepository';
import {
  ApprovalRequestActionReq,
  CreateGroupMemberConnectionApprovalRequestReq,
} from '../../dto/ReqDtoRepository';
import {
  ApprovalRequestListResponse,
  ApprovalRequestResponse,
} from '../../dto/ResDtoRepository';
import { ApprovalRequestStatus } from '../../enum/EnumRepository';
import { ErrorCode } from '../../api/errorCode';
import { useAuthStore } from '../../store/authStore';
import { useApprovalRequestStore } from '../../store/approvalRequestStore';
import { runCurrentAccountRequest } from '../../util/currentAccountRequest';
import {
  canShowOwnedResource,
  type ResourceStatus,
} from '../../util/scopedResource';
import { useApiWrapper } from './apiWrapper';

interface UseApprovalRequestStateResult {
  approvalRequestList: ApprovalRequestData[];
  listOwnerAui: string | null;
  status: ResourceStatus;
}

interface UseApprovalRequestActionsResult {
  getApprovalRequestList: () => Promise<void>;
  createGroupMemberConnectionApprovalRequest: (
    data: CreateGroupMemberConnectionApprovalRequestReq,
  ) => Promise<boolean>;
  acceptApprovalRequest: (data: ApprovalRequestActionReq) => Promise<boolean>;
  rejectApprovalRequest: (data: ApprovalRequestActionReq) => Promise<boolean>;
  cancelApprovalRequest: (data: ApprovalRequestActionReq) => Promise<boolean>;
  acknowledgeApprovalRequest: (data: ApprovalRequestActionReq) => Promise<boolean>;
  discardApprovalRequest: (requestId: string) => void;
  clearApprovalRequests: () => void;
}

type UseApprovalRequestResult =
  UseApprovalRequestStateResult & UseApprovalRequestActionsResult;

export const useApprovalRequestState = (): UseApprovalRequestStateResult => {
  const accountAui = useAuthStore((state) => state.user?.aui);
  const approvalRequestList = useApprovalRequestStore(
    (state) => state.approvalRequestList,
  );
  const listOwnerAui = useApprovalRequestStore((state) => state.listOwnerAui);
  const status = useApprovalRequestStore((state) => state.status);
  const canShowList = canShowOwnedResource({
    pageOwnerAui: accountAui,
    resourceOwnerAui: listOwnerAui,
    status,
  });

  return {
    approvalRequestList: canShowList ? approvalRequestList : [],
    listOwnerAui,
    status,
  };
};

export const useApprovalRequestActions = (): UseApprovalRequestActionsResult => {
  const accountAui = useAuthStore((state) => state.user?.aui);
  const beginApprovalRequestListFetch = useApprovalRequestStore(
    (state) => state.beginApprovalRequestListFetch,
  );
  const setApprovalRequestList = useApprovalRequestStore(
    (state) => state.setApprovalRequestList,
  );
  const setApprovalRequestListError = useApprovalRequestStore(
    (state) => state.setApprovalRequestListError,
  );
  const upsertApprovalRequest = useApprovalRequestStore(
    (state) => state.upsertApprovalRequest,
  );
  const removeApprovalRequest = useApprovalRequestStore(
    (state) => state.removeApprovalRequest,
  );
  const clearApprovalRequests = useApprovalRequestStore(
    (state) => state.clearApprovalRequests,
  );
  const withApiHandler = useApiWrapper();

  const handleGetSuccess = (
    response: ApprovalRequestListResponse,
    ownerAui: string,
  ) => {
    setApprovalRequestList(
      response.data.filter(
        (request) => request.status === ApprovalRequestStatus.PENDING
          || (
            request.status !== ApprovalRequestStatus.CANCELED
            && request.requester.aui === ownerAui
          ),
      ),
      ownerAui,
    );
  };

  const handleUpdateSuccess = (
    response: ApprovalRequestResponse,
    ownerAui: string,
  ): boolean => {
    upsertApprovalRequest(response.data, ownerAui);
    return true;
  };

  const refreshAfterStateConflict = (
    ownerAui: string,
  ) => (errorCode: ErrorCode) => {
    if (![
      ErrorCode.APR_NOT_FOUND,
      ErrorCode.APR_NOT_PENDING,
      ErrorCode.APR_EXPIRED,
      ErrorCode.APR_DUPLICATE,
      ErrorCode.APR_GROUP_MEMBER_ALREADY_LINKED,
      ErrorCode.APR_APPROVER_ALREADY_LINKED,
      ErrorCode.APR_CONFLICT,
    ].includes(errorCode)) return;

    void runCurrentAccountRequest(
      ownerAui,
      () => getApprovalRequestListApi(ownerAui),
      (response) => handleGetSuccess(response, ownerAui),
    ).catch(() => undefined);
  };

  const getApprovalRequestListHandler = async () => {
    if (!accountAui) return;
    const aui = accountAui;
    beginApprovalRequestListFetch(aui);

    const apiFunction = async () => {
      try {
        return await runCurrentAccountRequest(
          aui,
          () => getApprovalRequestListApi(aui),
          (response) => handleGetSuccess(response, aui),
        );
      } catch (error) {
        setApprovalRequestListError();
        throw error;
      }
    };

    await withApiHandler(apiFunction, [aui, 'get']);
  };

  const createGroupMemberConnectionApprovalRequestHandler = async (
    data: CreateGroupMemberConnectionApprovalRequestReq,
  ): Promise<boolean> => {
    if (!accountAui) return false;
    const aui = accountAui;
    const result = await withApiHandler(
      () => runCurrentAccountRequest(
        aui,
        () => createGroupMemberConnectionApprovalRequestApi(aui, data),
        (response) => handleUpdateSuccess(response, aui),
      ),
      [aui, data],
      { onError: refreshAfterStateConflict(aui) },
    );
    return result ?? false;
  };

  const handleApprovalRequestAction = async (
    action: 'accept' | 'reject' | 'cancel',
    data: ApprovalRequestActionReq,
  ): Promise<boolean> => {
    if (!accountAui) return false;
    const aui = accountAui;
    const actionApi = {
      accept: acceptApprovalRequestApi,
      reject: rejectApprovalRequestApi,
      cancel: cancelApprovalRequestApi,
    }[action];
    const result = await withApiHandler(
      () => runCurrentAccountRequest(
        aui,
        () => actionApi(aui, data),
        (response) => {
          if (action === 'cancel') {
            return handleUpdateSuccess(response, aui);
          }
          removeApprovalRequest(data.id);
          return true;
        },
      ),
      [aui, action, data],
      { onError: refreshAfterStateConflict(aui) },
    );
    return result ?? false;
  };

  const acknowledgeApprovalRequest = async (
    data: ApprovalRequestActionReq,
  ): Promise<boolean> => {
    if (!accountAui) return false;
    const aui = accountAui;
    const result = await withApiHandler(
      () => runCurrentAccountRequest(
        aui,
        () => acknowledgeApprovalRequestApi(aui, data),
        () => {
          removeApprovalRequest(data.id);
          return true;
        },
      ),
      [aui, 'acknowledge', data],
    );
    return result ?? false;
  };

  return {
    getApprovalRequestList: getApprovalRequestListHandler,
    createGroupMemberConnectionApprovalRequest:
      createGroupMemberConnectionApprovalRequestHandler,
    acceptApprovalRequest: (data) =>
      handleApprovalRequestAction('accept', data),
    rejectApprovalRequest: (data) =>
      handleApprovalRequestAction('reject', data),
    cancelApprovalRequest: (data) =>
      handleApprovalRequestAction('cancel', data),
    acknowledgeApprovalRequest,
    discardApprovalRequest: removeApprovalRequest,
    clearApprovalRequests,
  };
};

export const useApprovalRequest = (): UseApprovalRequestResult => ({
  ...useApprovalRequestState(),
  ...useApprovalRequestActions(),
});
